import { types, flow } from 'mobx-state-tree';
import {
  getAllUsers,
  inviteUsers,
  verifyInviteToken,
  verifyResetToken,
  claimAccount,
  deleteUserById,
  postPasswordReset,
  postPassword,
  updateUsers,
  deleteRoleFromUserByUuid,
} from '../api/users';
import { getAllRoles, createRole, deleteRole } from '../api/roles';
import { handleResponse } from '../api';
import { getStore } from './index';

const parseUser = userdata => ({
  ...userdata,
  name: userdata['username'],
  email: userdata['primary-email'],
});

const User = types
  .model('User', {
    uuid: types.optional(types.string, ''),
    name: types.optional(types.string, ''),
    email: types.optional(types.string, ''),
    status: types.optional(types.string, ''),
    roles: types.array(types.frozen()),
  })
  .views(self => ({
    get isAdmin() {
      return self.roles.some(
        role => role.toLowerCase() === 'admin' || role.toLowerCase() === 'zeus',
      );
    },
  }))
  .actions(self => ({
    save: () => {
      updateUsers([
        {
          uuid: self.uuid,
          roles: self.roles,
        },
      ]);
    },
    removeRole: role => {
      self.roles = self.roles.filter(r => r !== role);
    },
    setRoles: roles => {
      self.roles = roles;
    },
    deleteRoles: roles => {
      return Promise.all(roles.map(r => deleteRoleFromUserByUuid(self.uuid, r)))
    }
  }));

const Role = types.model('Role', {
  title: types.optional(types.string, ''),
  permissions: types.array(types.string),
});

const UserStore = types
  .model('UserStore', {
    roles: types.array(Role),
    users: types.array(User),
    tokenExpired: types.optional(types.boolean, false),
    registrationSuccessfull: types.optional(types.boolean, false),
    sendPasswordResetSuccesfull: types.optional(types.boolean, false),
    resetPasswordSuccess: types.optional(types.boolean, false),
  })
  .actions(self => {
    const setRoles = rolesdata => {
      self.roles = rolesdata;
    };

    const setUsers = userdata => {
      self.users = userdata.map(user => parseUser(user));
    };

    const addUser = user => {
      self.users.push(parseUser(user));
    };

    const removeUser = uuid => {
      self.users = self.users.filter(u => u.uuid !== uuid);
    };

    const updateUser = user => {
      const index = self.users.findIndex(u => u.email === user);
      if (index === -1) {
        addUser(user);
      } else {
        self.users[index] = parseUser(user);
      }
    };

    const loadAllRoles = flow(function*() {
      const response = yield getAllRoles();
      handleResponse([
        [
          200,
          () => {
            setRoles(response.data);
          },
        ],
        [
          'default',
          () => {
            console.log('error loading roles');
          },
        ],
      ])(response);
      return response;
    });

    const loadAllUsers = flow(function*() {
      const response = yield getAllUsers();
      handleResponse([
        [
          200,
          () => {
            setUsers(response.data);
          },
        ],
        [
          'default',
          () => {
            console.log('error loading users');
          },
        ],
      ])(response);
      return response;
    });

    const inviteUser = flow(function*(email, roles) {
      getStore('appStore').setIsLoading(true);
      const response = yield inviteUsers([
        {
          'primary-email': email,
          roles,
        },
      ]);
      handleResponse([
        [
          200,
          () => {
            getStore('appStore').setIsLoading(false);
            // TODO: response is strangely formatted in the backend.
            // it wraps all response data inside data (therefore data.data) :(
            // response.data.data.map(user => {
            //   addUser(user.invitee);
            // });
            // it also doesn't include the uuid of the user and
            // therefore we need to load all users again
            loadAllUsers();
            getStore(
              'notificationStore',
            ).raiseInfo('notifications.inviteUser.success', { email });
          },
        ],
        [
          'default',
          () => {
            console.log('error inviting user');
            getStore('appStore').setIsLoading(false);
            getStore('notificationStore').raiseError(
              'notifications.inviteUser.error',
            );
          },
        ],
      ])(response);
      return response;
    });

    const verifyInvite = flow(function*(email, token) {
      const response = yield verifyInviteToken(email, token);
      handleResponse([
        [
          200,
          () => {
            console.log('Token is valid');
          },
        ],
        [
          'default',
          () => {
            self.tokenExpired = true;
            getStore('notificationStore').raiseError(
              'notifications.inviteTokenExpired',
            );
          },
        ],
      ])(response);
      return response;
    });

    const verifyReset = flow(function*(email, token) {
      const response = yield verifyResetToken(email, token);
      handleResponse([
        [
          200,
          () => {
            console.log('Token is valid');
          },
        ],
        [
          'default',
          () => {
            self.tokenExpired = true;
            getStore('notificationStore').raiseError(
              'notifications.resetTokenExpired',
            );
          },
        ],
      ])(response);
      return response;
    });

    const submitRegistration = flow(function*(userData) {
      getStore('appStore').setIsLoading(true);
      const response = yield claimAccount(userData);
      handleResponse([
        [
          200,
          () => {
            getStore('appStore').setIsLoading(false);
            // TODO: backend currently only returns (email, uuid, name) upon
            // registration. it should return the whole user. so we can just
            // add it the loaded users. for now we will reload all users...
            // updateUser(response.data);
            loadAllUsers();
            self.registrationSuccessfull = true;
            console.log('registration successfull');
          },
        ],
        [
          'default',
          () => {
            getStore('appStore').setIsLoading(false);
            getStore('notificationStore').raiseError(
              'notifications.claimAccountError',
            );
          },
        ],
      ])(response);
      return response;
    });

    const deleteUser = flow(function*(uuid) {
      getStore('appStore').setIsLoading(true);
      const response = yield deleteUserById(uuid);
      const user = self.getUser(uuid);
      handleResponse([
        [
          200,
          () => {
            getStore('appStore').setIsLoading(false);
            removeUser(uuid);
            getStore('notificationStore').raiseInfo(
              'notifications.deleteUser.success',
              {
                name: user.name || user.email,
              },
            );
            console.log('deleted user');
          },
        ],
        [
          'default',
          () => {
            getStore('appStore').setIsLoading(false);
            // TODO: get name from uuid
            getStore('notificationStore').raiseError(
              'notifications.deleteUser.error',
              {
                name: user.name || user.email,
              },
            );
          },
        ],
      ])(response);
      return response;
    });

    const sendPasswordReset = flow(function*(email) {
      getStore('appStore').setIsLoading(true);
      const response = yield postPasswordReset(email);
      handleResponse([
        [
          200,
          () => {
            getStore('appStore').setIsLoading(false);
            getStore(
              'notificationStore',
            ).raiseInfo('notifications.sendPasswordReset.success', { email });
            self.sendPasswordResetSuccesfull = true;
          },
        ],
        [
          'default',
          () => {
            getStore('appStore').setIsLoading(false);
            // TODO: get name from uuid
            getStore(
              'notificationStore',
            ).raiseError('notifications.sendPasswordReset.error', { email });
            self.sendPasswordResetSuccesfull = false;
          },
        ],
      ])(response);
      return response;
    });

    const resetPassword = flow(function*(email, token, password, confirmation) {
      getStore('appStore').setIsLoading(true);
      const response = yield postPassword({
        'confirm-new-password': confirmation,
        'new-password': password,
        nonce: token,
        email,
      });
      handleResponse([
        [
          200,
          () => {
            getStore('appStore').setIsLoading(false);
            getStore('notificationStore').raiseInfo(
              'notifications.resetPassword.success',
            );
            self.resetPasswordSuccess = true;
          },
        ],
        [
          'default',
          () => {
            getStore('appStore').setIsLoading(false);
            // TODO: get name from uuid
            getStore('notificationStore').raiseError(
              'notifications.resetPassword.error',
            );
            self.resetPasswordSuccess = false;
          },
        ],
      ])(response);
      return response;
    });

    const handleCreateRole = role => {
      createRole(role);
    };

    const handleDeleteRole = role => {
      deleteRole(role);
    };

    return {
      handleCreateRole,
      handleDeleteRole,
      loadAllRoles,
      loadAllUsers,
      inviteUser,
      deleteUser,
      verifyInvite,
      verifyReset,
      submitRegistration,
      sendPasswordReset,
      resetPassword,
    };
  })
  .views(self => ({
    getUser: uuid => {
      return self.users.find(u => u.uuid === uuid);
    },
  }));

export { UserStore, User, parseUser };
